import React, { useState } from 'react';
import { Box, Typography, Button, Paper, Divider, Alert, Accordion, AccordionSummary, AccordionDetails, TextField } from '@mui/material';
import ScienceIcon from '@mui/icons-material/Science';
import BiotechIcon from '@mui/icons-material/Biotech';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useDashboard } from '../../../contexts/DashboardContext';

interface MastitisTestStep1Props {
  onNext?: (file: File, collectionDate?: string) => void;
}

const MastitisTestStep1: React.FC<MastitisTestStep1Props> = ({ onNext }) => {
  const { updateContent } = useDashboard();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [collectionDate, setCollectionDate] = useState<string | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
    if (file) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    } else {
      setPreviewUrl(null);
    }
  };

  const handleNext = () => {
    if (selectedFile && onNext) {
      onNext(selectedFile, collectionDate || undefined);
    }
  };

  const handleCancel = () => {
    updateContent('welcome');
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2 }}>
      {/* Header com título e ação de cancelar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Teste de Mastite</Typography>
        <Button variant="text" size="small" onClick={handleCancel} sx={{ color: 'text.secondary', textTransform: 'none' }}>
          Cancelar
        </Button>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2,
          height: '100%',
        }}
      >
        {/* Coluna Esquerda */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Exemplo de Placa para Teste de Mastite
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: 560 }}>
            O teste de mastite é utilizado para identificar a presença de microrganismos associados à mastite no leite.
            Ele ajuda na análise rápida e na orientação da conduta adequada. Para resultados confiáveis, é essencial
            fotografar a placa corretamente, com boa iluminação e foco, evitando reflexos e inclinações.
          </Typography>
          <Box
            component="img"
            src="/mastitis-imgs/example-mastitis.png"
            alt="Exemplo de placa para teste de mastite"
            sx={{
              maxWidth: '100%',
              maxHeight: { xs: 280, md: 400 },
              objectFit: 'contain',
              borderRadius: 2,
            }}
          />
        </Paper>

        {/* Coluna Direita: pré-resultado, dicas e upload */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            justifyContent: 'flex-start',
            gap: 2,
            height: '100%',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <ScienceIcon fontSize="small" color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Pré-resultado</Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
            As análises parciais serão exibidas nas próximas etapas, conforme suas seleções.
          </Typography>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BiotechIcon color="info" />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Dicas laboratoriais</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                • Evite sombras e reflexos ao fotografar.
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                • Centralize a placa e mantenha o foco.
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                • Utilize fundo neutro para melhor contraste.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Divider />

          {/* Campo de data da coleta */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Data da coleta
            </Typography>
            <TextField
              type="date"
              size="small"
              fullWidth
              value={collectionDate || ''}
              onChange={(e) => setCollectionDate(e.target.value || null)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Envie a imagem da sua placa de teste
          </Typography>

          {/* Campo de Upload */}
          <Box>
            <Button variant="outlined" component="label" sx={{ textTransform: 'none' }}>
              Selecionar imagem
              <input type="file" hidden accept="image/*" onChange={handleFileChange} />
            </Button>
          </Box>

          {/* Pré-visualização */}
          {previewUrl && (
            <Box
              sx={{
                mt: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 160,
              }}
            >
              <Box
                component="img"
                src={previewUrl}
                alt="Pré-visualização da imagem enviada"
                sx={{ maxWidth: '100%', maxHeight: 240, objectFit: 'contain', borderRadius: 1 }}
              />
            </Box>
          )}

          {/* Botão Próximo */}
          <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'flex-end', pb: 1 }}>
            <Button
              variant="contained"
              disabled={!selectedFile}
              onClick={handleNext}
              sx={(theme) => ({
                color: '#fff',
                '&.Mui-disabled': {
                  color: '#fff',
                  backgroundColor: theme.palette.action.disabledBackground,
                },
              })}
            >
              Próximo
            </Button>
          </Box>
          {!selectedFile && (
            <Alert severity="info" sx={{ textAlign: 'center' }}>
              Envie a foto da placa para continuar.
            </Alert>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default MastitisTestStep1;
